"use client";

import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import { Heading } from "@/app/design-system/atoms/Heading";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type Profil = {
  id?: number;
  documentId?: string;
  label?: string | null;
  description?: string | null;
  image?: Media | null;
};

type MetierBlock = StrapiBlock & {
  __component: "blocks.metier";
  titre?: string | null;
  profils?: Profil[];
};

export default function Metier({ data }: BlockProps<MetierBlock>) {
  const profils = Array.isArray(data.profils) ? data.profils : [];
  const [activeIndex, setActiveIndex] = useState(0);

  if (profils.length === 0) return null;

  const previous = () => {
    setActiveIndex((prev) =>
      prev === 0 ? profils.length - 1 : prev - 1
    );
  };

  const next = () => {
    setActiveIndex((prev) =>
      prev === profils.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="bg-white py-14 lg:py-20">
      <div className="lms-container">

        {/* TITRE */}
        {data.titre && (
          <Heading
            as="h2"
            className="mb-10 text-center font-playfair text-4xl font-bold text-[#343434] lg:mb-14 lg:text-[44px] 2xl:text-5xl"
          >
            {data.titre}
          </Heading>
        )}

        {/* ================= MOBILE SLIDER ================= */}
        <div className="lg:hidden">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${activeIndex * 100}%)` }}
            >
              {profils.map((profil, index) => (
                <div
                  key={profil.documentId ?? profil.id ?? index}
                  className="w-full shrink-0 px-2"
                >
                  <ProfilCard profil={profil} mobile />
                </div>
              ))}
            </div>
          </div>

          {/* NAVIGATION */}
          {profils.length > 1 && (
            <div className="mt-7 flex items-center justify-center gap-5">
              <button
                type="button"
                onClick={previous}
                aria-label="Métier précédent"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen"
              >
                <ChevronLeft size={20} />
              </button>

              <div className="flex items-center gap-2">
                {profils.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Afficher le métier ${index + 1}`}
                    className={`h-2.5 rounded-full transition-all duration-300 ${activeIndex === index
                        ? "w-7 bg-primaryGreen"
                        : "w-2.5 bg-greenPastel"
                      }`}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={next}
                aria-label="Métier suivant"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-primaryGreen text-primaryGreen"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>

        {/* ================= DESKTOP ================= */}
        <div className="mx-auto hidden max-w-[1250px] items-stretch gap-8 lg:grid lg:grid-cols-3 lg:gap-10">
          {profils.map((profil, index) => (
            <ProfilCard
              key={profil.documentId ?? profil.id ?? index}
              profil={profil}
            />
          ))}
        </div>

      </div>
    </section>
  );
}

function ProfilCard({
  profil,
  mobile = false,
}: {
  profil: Profil;
  mobile?: boolean;
}) {
  const imageUrl = profil.image?.url || "";
  const imageAlt =
    profil.image?.alternativeText ||
    profil.label ||
    "Métier proposé";

  return (
    <article
      className={`group flex items-end px-5 py-6 transition-all duration-300 ${mobile
          ? "mx-auto min-h-[230px] max-w-[390px] rounded-2xl bg-white"
          : "min-h-[200px] hover:rounded-2xl hover:bg-white hover:shadow-[0_12px_35px_rgba(0,0,0,0.12)]"
        }`}
    >
      <div
        className={`grid w-full items-end gap-4 ${mobile
            ? "grid-cols-[120px_1fr]"
            : "grid-cols-[150px_1fr] gap-5"
          }`}
      >

        {/* IMAGE */}
        <div
          className={`relative w-full ${mobile ? "h-[180px]" : "h-[200px]"
            }`}
        >
          {imageUrl && (
            <Image
              src={imageUrl}
              alt={imageAlt}
              fill
              className="object-contain object-bottom"
              sizes={mobile ? "120px" : "200px"}
            />
          )}
        </div>

        {/* CONTENU */}
        <div className={mobile ? "pb-3" : "pb-5"}>
          {profil.label && (
            <h3
              className={`font-playfair font-bold leading-tight text-primaryGreen ${mobile ? "text-xl" : "text-2xl"
                }`}
            >
              {profil.label}
            </h3>
          )}

          <div
            className={`rounded-full bg-gradient-to-r from-primaryGreen to-greenPastel ${mobile
                ? "my-4 h-[3px] w-14"
                : "my-5 h-[3px] w-20"
              }`}
          />

          {profil.description && (
            <p
              className={`leading-snug text-textColor ${mobile ? "text-sm" : "text-lg"
                }`}
            >
              {profil.description}
            </p>
          )}
        </div>

      </div>
    </article>
  );
}